/**
 * TPO block renderer — the visual half of the Market Profile chart style.
 *
 * Each session's profile owns its FULL column span. TPO is a chart STYLE, not an
 * overlay: the candles are replaced by a thin close-price line, so nothing
 * competes for the space and the profile does not need to clamp to a fraction of
 * the session the way the session volume profile does.
 *
 * Level of detail falls out of zoom rather than being a mode. Block width is
 * `sessionWidth / maxCount`, so as the chart zooms out every block shrinks; once
 * blocks get small the 1px separator is dropped and each row's segments become
 * contiguous. Adjacent solid rects of adjacent hues then read as one smooth
 * gradient bar — the same code path, one variable, producing both the crisp
 * blocks when zoomed in and the blended colour graph when zoomed out.
 *
 * Deliberately avoids canvas gradients (`createLinearGradient`) so the whole
 * renderer stays assertable through the fake-ctx harness the other profile
 * draws use.
 */

import type { TpoProfile } from './tpoProfile'
import type { VolumeProfile } from './volumeProfile'
import {
  DEFAULT_TPO_SETTINGS,
  bracketLetter,
  inkOn,
  pctToAlpha,
  resolveBlockAlpha,
  resolveBlockColor,
  type TpoLevelStyle,
  type TpoLineStyle,
  type TpoSettings,
} from './tpoSettings'

/**
 * Separator taper. Rather than snapping between "1px gap" and "no gap" at a
 * single threshold — which pops visibly as you zoom through it — the gap shrinks
 * continuously: full width at or above SEPARATE_ABOVE_PX, gone at or below
 * MERGE_BELOW_PX, linearly interpolated between.
 *
 * The window is deliberately wide and generous. Block width is
 * `sessionWidth / bracketCount`, and bracketCount is a property of the DATA
 * (~13 for a 6.5h session at 30m brackets), not of zoom — so blocks only get
 * narrow when a session is squeezed into a few dozen pixels. A tight threshold
 * put the blend outside the chart's reachable zoom-out range entirely.
 */
const MERGE_BELOW_PX = 5
const SEPARATE_ABOVE_PX = 11

/** Separator between adjacent blocks, in px, at full separation. */
const BLOCK_GAP = 1

/**
 * Gap width for a given block size — 0 when merged, BLOCK_GAP when fully
 * separated, tapering in between. Driven by the smaller dimension so a row that
 * is short but wide (or vice versa) still blends.
 */
export function blockGap(blockWidth: number, blockHeight: number): number {
  const size = Math.min(blockWidth, blockHeight)
  if (size <= MERGE_BELOW_PX) return 0
  if (size >= SEPARATE_ABOVE_PX) return BLOCK_GAP
  return (BLOCK_GAP * (size - MERGE_BELOW_PX)) / (SEPARATE_ABOVE_PX - MERGE_BELOW_PX)
}

/** The narrow canvas surface this renderer needs — fake-ctx testable. */
export type TpoCtx = {
  fillStyle: string | CanvasGradient | CanvasPattern
  globalAlpha: number
  /**
   * Needed because this renderer runs inside a `zLevel: -1` indicator, which
   * klinecharts paints with `destination-over` — later draws go BEHIND earlier
   * ones. Blocks want that (it keeps them under the price line), but anything
   * meant to sit ON a block must opt back into `source-over` or it is invisible.
   */
  globalCompositeOperation: string
  save: () => void
  restore: () => void
  fillRect: (x: number, y: number, w: number, h: number) => void
}

/** True when blocks read as discrete units rather than a blended bar. Derived
 *  from the taper so there is a single source of truth for the transition. */
export function shouldSeparateBlocks(blockWidth: number, blockHeight: number): boolean {
  return blockGap(blockWidth, blockHeight) > 0
}

/**
 * Legibility floor for a bracket letter, derived from the glyph rather than
 * guessed.
 *
 * Width is the binding constraint. Row height is roughly `paneHeight / rowCount`
 * and the tick targets ~60 rows, so it barely moves with zoom — but block width is
 * `sessionWidth / count`, which collapses as you zoom out. Once a block is
 * narrower than the glyph, adjacent letters overlap into an unreadable smear, so
 * the width test measures the WIDEST letter and the letters drop out as soon as
 * they stop fitting.
 *
 * 'W' is measured rather than the letter actually being drawn, so a row does not
 * show a narrow 'I' while hiding a wide 'W' beside it.
 */
const LETTER_WIDTH_PAD_PX = 2
/** Height floor: the 10px font plus a little, so glyphs clear adjacent rows. */
const MIN_LETTER_HEIGHT_PX = 9

/**
 * Whether a bracket letter is legible in a block of this size. Exported so the
 * threshold is testable without a chart.
 */
export function letterFits(
  ctx: Pick<TpoChromeCtx, 'measureText'>,
  blockWidth: number,
  blockHeight: number,
): boolean {
  if (blockHeight < MIN_LETTER_HEIGHT_PX) return false
  return blockWidth >= ctx.measureText('W').width + LETTER_WIDTH_PAD_PX
}

export interface TpoPaintParams {
  profile: TpoProfile
  /** Left edge of this session's column span. */
  leftX: number
  /** Right edge of this session's column span. */
  rightX: number
  /** Style settings; defaults applied when omitted. */
  settings?: TpoSettings
}

/**
 * Paint one session's TPO profile into its column span.
 *
 * `display` decides the horizontal meaning of a block:
 *  - `stacked` — blocks collapse to the left edge, so row length IS the count
 *    and the most-visited row exactly fills the span.
 *  - `expanded` — each bracket keeps its own time column, so a block sits under
 *    the bracket that made it and gaps show where a bracket never traded that
 *    price. Width divides by bracketCount rather than the max count, because
 *    the columns are time slots, not a tally.
 */
export function paintTpoProfile(
  ctx: TpoChromeCtx,
  yAxis: { convertToPixel: (value: number) => number },
  params: TpoPaintParams,
): void {
  const { profile, leftX, rightX } = params
  const settings = params.settings ?? DEFAULT_TPO_SETTINGS
  const sessionWidth = rightX - leftX
  if (!(sessionWidth > 0)) return
  if (!settings.blocks && !settings.letters) return

  // The POC row holds the max count by construction of `pointOfControl`.
  const maxCount = profile.rows[profile.pocIdx]?.count ?? 0
  if (maxCount <= 0) return

  const expanded = settings.display === 'expanded'
  const columns = expanded ? Math.max(profile.bracketCount, 1) : maxCount
  const blockWidth = sessionWidth / columns

  ctx.save()
  ctx.font = LABEL_FONT
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  /**
   * One row's height on the GRID, i.e. a full tick — not any particular row's
   * measured height.
   *
   * The outer rows are clamped to the session's real high/low, so they are
   * thinner than a tick. Measuring per row therefore flipped the letter decision
   * on those rows alone and produced a profile with letters on some rows and not
   * others. Both the letter test and the separator taper key off this single
   * value, so a profile is uniformly lettered-and-separated or neither.
   */
  const gridRowHeight = Math.abs(
    yAxis.convertToPixel(profile.rows[0].priceLow) -
      yAxis.convertToPixel(profile.rows[0].priceLow + profile.tickSize),
  )
  // Resolved ONCE per profile — never per row.
  const lettersFit = settings.letters && letterFits(ctx, blockWidth, gridRowHeight)
  const gap = blockGap(blockWidth, gridRowHeight)

  profile.rows.forEach((row, rowIdx) => {
    if (row.count === 0) return
    const yTop = yAxis.convertToPixel(row.priceHigh)
    const yBottom = yAxis.convertToPixel(row.priceLow)
    const blockHeight = Math.max(yBottom - yTop - gap, 0)
    const inValueArea = rowIdx >= profile.valIdx && rowIdx <= profile.vahIdx

    /**
     * The POC row is tinted per BLOCK, never painted as one continuous span.
     *
     * A full-width bar filled the time gaps between brackets, which in expanded
     * mode erases the very structure that mode exists to show. Colouring each
     * block instead gives the same read in stacked mode — the POC is the
     * max-count row, so its blocks already reach the full width — while leaving
     * expanded mode's gaps intact.
     */
    const isPocRow = rowIdx === profile.pocIdx && settings.poc.visible

    const alpha = resolveBlockAlpha(settings, inValueArea)
    const w = Math.max(blockWidth - gap, 0)

    row.brackets.forEach((bracket, i) => {
      // Stacked positions by ordinal within the row; expanded by the bracket's
      // own index, which is what turns the histogram into a time-development
      // view. Storing bracket identity per row is what makes this a one-liner.
      //
      // Expanded subtracts `bracketMin` because brackets are anchored to the
      // regular open and go negative in pre-market — using the raw index would
      // paint those columns to the LEFT of the session's own span.
      const x = leftX + (expanded ? bracket - profile.bracketMin : i) * blockWidth
      const color = isPocRow
        ? settings.poc.color
        : resolveBlockColor(
            settings,
            bracket,
            profile.bracketCount,
            inValueArea,
            profile.bracketMin,
          )

      if (settings.blocks) {
        ctx.fillStyle = color
        ctx.globalAlpha = alpha
        ctx.fillRect(x, yTop, w, blockHeight)
      }
      if (lettersFit) {
        // `source-over` for the glyph only: under the inherited `destination-over`
        // the letter would be painted BEHIND the block just drawn, i.e. invisible.
        const baseOp = ctx.globalCompositeOperation
        ctx.globalCompositeOperation = 'source-over'
        // Ink is picked against the fill the glyph actually lands on, so the
        // near-white POC row gets dark letters automatically.
        ctx.fillStyle = settings.blocks ? inkOn(color) : color
        ctx.globalAlpha = alpha
        ctx.fillText(
          // The scheme is what makes regular-session brackets uppercase from A
          // and extended ones lowercase; null falls back to a flat A–Z, a–z run.
          bracketLetter(bracket, profile.bracketScheme ?? undefined),
          x + w / 2,
          yTop + blockHeight / 2,
        )
        ctx.globalCompositeOperation = baseOp
      }
    })
  })
  ctx.restore()
}

// ─── Level chrome (VAH / POC / VAL) ──────────────────────────────────────────

/** Canvas dash pattern per configured line style. */
const DASH_PATTERNS: Record<TpoLineStyle, number[]> = {
  solid: [],
  dashed: [4, 4],
  dotted: [1, 3],
}

/**
 * Height and depth of a level marker triangle, in px.
 *
 * Kept below a row's height (~11px at the default row count) so a marker reads as
 * a pointer at one level rather than a block spanning several.
 */
const MARKER_SIZE_PX = 5

/** Gap between a marker's tip and the profile's left edge. */
const MARKER_GAP_PX = 2

const LABEL_FONT = '10px IBM Plex Sans, ui-sans-serif, system-ui, sans-serif'

/** Canvas surface the chrome needs — text and dashes on top of TpoCtx. */
export type TpoChromeCtx = TpoCtx & {
  strokeStyle: string | CanvasGradient | CanvasPattern
  lineWidth: number
  font: string
  textAlign: string
  textBaseline: string
  beginPath: () => void
  moveTo: (x: number, y: number) => void
  lineTo: (x: number, y: number) => void
  stroke: () => void
  setLineDash: (segments: number[]) => void
  closePath: () => void
  fill: () => void
  fillText: (text: string, x: number, y: number) => void
  measureText: (text: string) => { width: number }
}

/**
 * Draw the Market Profile levels over one session: dashed value-area boundaries
 * (and optional initial balance), plus a small left-side marker per level.
 *
 * Split from paintTpoProfile so the blocks for every session paint first and the
 * levels sit on top — otherwise a later session's blocks would cover an earlier
 * session's guides.
 */
export function paintTpoLevels(
  ctx: TpoChromeCtx,
  yAxis: { convertToPixel: (value: number) => number },
  params: TpoPaintParams,
): void {
  const { profile, leftX, rightX } = params
  const settings = params.settings ?? DEFAULT_TPO_SETTINGS
  if (!(rightX - leftX > 0)) return

  const rowMid = (idx: number) => {
    const row = profile.rows[idx]
    if (!row) return null
    return yAxis.convertToPixel((row.priceLow + row.priceHigh) / 2)
  }

  interface Level {
    y: number
    style: TpoLevelStyle
    /**
     * Whether a horizontal rule is drawn across the session.
     *
     * The POC does NOT get one: its row is already painted in the POC colour, so a
     * rule through it is redundant ink over the blocks it is meant to point out.
     * Its marker alone carries it.
     */
    rule: boolean
  }
  const levels: Level[] = []

  const push = (y: number | null, style: TpoLevelStyle, rule: boolean) => {
    if (y === null || !Number.isFinite(y) || !style.visible) return
    levels.push({ y, style, rule })
  }

  push(rowMid(profile.vahIdx), settings.valueArea, true)
  push(rowMid(profile.pocIdx), settings.poc, false)
  push(rowMid(profile.valIdx), settings.valueArea, true)
  // Initial balance is a PRICE pair, not a row pair — it comes from the opening
  // brackets' high/low rather than from a binned row.
  if (profile.initialBalance) {
    push(yAxis.convertToPixel(profile.initialBalance.high), settings.initialBalance, true)
    push(yAxis.convertToPixel(profile.initialBalance.low), settings.initialBalance, true)
  }
  if (levels.length === 0) return

  ctx.save()
  // Levels annotate the profile, so they must sit ON it — same destination-over
  // trap as the block letters. ctx.restore() puts the inherited op back.
  ctx.globalCompositeOperation = 'source-over'

  for (const level of levels) {
    if (!level.rule) continue
    ctx.setLineDash(DASH_PATTERNS[level.style.lineStyle] ?? [])
    ctx.strokeStyle = level.style.color
    ctx.lineWidth = 1
    ctx.globalAlpha = 1
    ctx.beginPath()
    ctx.moveTo(leftX, level.y)
    ctx.lineTo(rightX, level.y)
    ctx.stroke()
  }
  ctx.setLineDash([])

  /**
   * Markers are small right-pointing triangles at the true level price.
   *
   * Deliberately NOT nudged apart the way text labels were: an arrow's whole job
   * is to point at a price, so displacing it to avoid a neighbour would make it
   * point at the wrong one. Two coincident levels overlap, which is honest — they
   * are at the same price.
   */
  if (settings.levelLabels) {
    for (const level of levels) {
      ctx.fillStyle = level.style.color
      ctx.globalAlpha = 1
      const tipX = leftX - MARKER_GAP_PX
      const backX = tipX - MARKER_SIZE_PX
      ctx.beginPath()
      ctx.moveTo(tipX, level.y)
      ctx.lineTo(backX, level.y - MARKER_SIZE_PX / 2)
      ctx.lineTo(backX, level.y + MARKER_SIZE_PX / 2)
      ctx.closePath()
      ctx.fill()
    }
  }

  ctx.restore()
}

/** Minimum visible length for a row that traded, so low-volume rows still read. */
const MIN_VOLUME_BAR_PX = 1

export interface TpoVolumeBandParams {
  /** Volume binned onto the TPO's own grid — see `binVolumeOnGrid`. */
  profile: VolumeProfile
  /** The TPO rows the band mirrors, supplying the y edges. */
  rows: TpoProfile['rows']
  /** Left edge of the band (where the TPO's span ends). */
  leftX: number
  /** Right edge of the band, i.e. the session's right edge. */
  rightX: number
  settings?: TpoSettings
}

/**
 * Paint the volume profile that sits beside a session's TPO.
 *
 * Bars are anchored on the RIGHT and grow leftward, mirroring the TPO's
 * left-anchored blocks. That leaves a gutter down the middle of the session at
 * every zoom, so the two profiles stay visually separable instead of colliding
 * as either one lengthens.
 *
 * Bucket i corresponds to row i by construction — the caller bins onto the TPO's
 * grid — so this never has to match prices back up.
 */
export function paintTpoVolumeBand(
  ctx: TpoChromeCtx,
  yAxis: { convertToPixel: (value: number) => number },
  params: TpoVolumeBandParams,
): void {
  const { profile, rows, leftX, rightX } = params
  const settings = params.settings ?? DEFAULT_TPO_SETTINGS
  const bandWidth = rightX - leftX
  if (!(bandWidth > 0)) return

  // The POC bucket holds the max volume by construction of `pointOfControl`.
  const maxTotal = profile.buckets[profile.pocIdx]?.total ?? 0
  if (maxTotal <= 0) return

  const alpha = pctToAlpha(settings.profileOpacity)

  ctx.save()
  profile.buckets.forEach((bucket, i) => {
    if (bucket.total <= 0) return
    const row = rows[i]
    if (!row) return

    const yTop = yAxis.convertToPixel(row.priceHigh)
    const yBottom = yAxis.convertToPixel(row.priceLow)
    const h = yBottom - yTop
    if (!(h > 0)) return

    const w = Math.max((bucket.total / maxTotal) * bandWidth, MIN_VOLUME_BAR_PX)
    // The band's OWN point of control, which need not be the TPO's — where the
    // most time was spent and where the most volume traded are different
    // questions, and seeing them disagree is the reason to show both.
    ctx.fillStyle =
      i === profile.pocIdx ? settings.volumeProfile.pocColor : settings.volumeProfile.color
    ctx.globalAlpha = alpha
    ctx.fillRect(rightX - w, yTop, w, h)
  })
  ctx.restore()
}
